import React, { ChangeEvent, useRef, useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import axios, { AxiosError } from "axios"
import { toast } from "react-toastify"
import swal from "sweetalert2"
import { useDeclarationUpdate } from "../DeclarationUpdate"
import {
     declarationAddFactureRequest,
     declarationDeleteFactureRequest,
     declarationGetFacturesRequest,
     declarationSetNbJoursTravaillesParStPortageOuSS,
} from "../../../../core/_request"
import {
     API_EXTRACT_HT_TVA_FROM_INVOICE_URL,
     API_URL_ESPACE_AVENTURIER,
     CONST_CONSULTANT_STATUT_ST_PORTAGE,
     CONST_CONSULTANT_STATUT_ST_SOCIETE_SERVICE,
} from "../../../../../../../constants"
import { getExtensionAccordingToMimetype } from "../../../../../../../helpers/helper-global"

import MySimpleSpinner from "../../../../../../../utils/MySimpleSpinner"
import Footer from "./_Footer"

import Dropzone from "./components/Dropzone"
import PendingQueue from "./components/PendingQueue"
import FacturesHistoryList from "./components/FacturesHistoryList"
import { IConsultantDeclarationMensuelleFactureModel } from "@common-models/*"

interface PendingFile {
     file: File
     ht: number | ""
     tva: number | ""
}

interface IInvoiceFileResult {
     items: { amountHt: number; vat: number }[]
     totalAmountHt: number
     totalVat: number
}

interface IExtractionResponse {
     invoices: IInvoiceFileResult[]
     totalAmountHt: number
     totalVat: number
}

const DeclarationUpdateFactures = ({ isEditionModeEnabled }: { isEditionModeEnabled: boolean }) => {
     const { data, onChangeNbJoursTravaillesCallback, setData, onCRUDFacturesPrestationCallback } = useDeclarationUpdate()
     const [nbJoursTravailles, setNbJoursTravailles] = useState<string | undefined>(data.declaration?.nbJoursTravailles?.toString())
     const [isNbJoursInputBegingEdited, setIsNbJoursInputBegingEdited] = useState(false)

     function handleSetNbJoursTravailles(e: React.ChangeEvent<HTMLInputElement>) {
          const value = e.target.value.replace(",", ".")
          if (/^[0-9]*[.,]?[0-9]*$/.test(value)) {
               setNbJoursTravailles(value)
               if (onChangeNbJoursTravaillesCallback) onChangeNbJoursTravaillesCallback(Number(value))
          }
     }

     function handleConfirmSetNbJoursTravailles(value: number) {
          setIsNbJoursInputBegingEdited(true)
          declarationSetNbJoursTravaillesParStPortageOuSS(
               data.declaration?.consultant?.id!,
               data.declaration?.mois!.format("MM")!,
               data.declaration?.mois!.format("YYYY")!,
               Number(value!)
          ).then(() => {
               setData(prev => ({
                    ...prev,
                    declaration: prev.declaration ? { ...prev.declaration, nbJoursTravailles: Number(value) } : null,
               }))
               setIsNbJoursInputBegingEdited(false)
          })
     }

     return (
          <>
               {[CONST_CONSULTANT_STATUT_ST_PORTAGE, CONST_CONSULTANT_STATUT_ST_SOCIETE_SERVICE].includes(data.declaration?.consultant?.statut!) && (
                    <>
                         <div className={"d-flex justify-content-center align-items-center gap-2"}>
                              <label className="fw-bold text-gray-700 fs-6">Nb jours travaillés :</label>
                              <div className="position-relative">
                                   <input
                                        value={nbJoursTravailles}
                                        style={{ width: "10rem" }}
                                        placeholder={"à renseigner"}
                                        type={"text"}
                                        onChange={handleSetNbJoursTravailles}
                                        onKeyDown={e => {
                                             if (e.key === "Enter" && Number(nbJoursTravailles) !== data.declaration?.nbJoursTravailles) {
                                                  handleConfirmSetNbJoursTravailles(Number(nbJoursTravailles))
                                             }
                                        }}
                                        className={"form-control form-control-solid text-center fw-bold"}
                                        disabled={!isEditionModeEnabled || isNbJoursInputBegingEdited}
                                   />
                                   {isNbJoursInputBegingEdited && (
                                        <div className="position-absolute top-50 start-50 translate-middle">
                                             <span className="spinner-border spinner-border-sm text-primary"></span>
                                        </div>
                                   )}
                              </div>
                              {Number(nbJoursTravailles) !== data.declaration?.nbJoursTravailles && !isNbJoursInputBegingEdited && (
                                   <button
                                        type={"button"}
                                        className={"btn btn-icon btn-sm btn-light-success rounded-circle shadow-sm"}
                                        onClick={() => handleConfirmSetNbJoursTravailles(Number(nbJoursTravailles))}
                                   >
                                        <i className={"fas fa-check fs-6"} />
                                   </button>
                              )}
                         </div>
                         <div className="separator border-secondary border-opacity-25 my-8" />
                    </>
               )}
               <Factures isEditionModeEnabled={isEditionModeEnabled} onCRUDFacturesPrestationCallback={onCRUDFacturesPrestationCallback} />
               <div className="separator separator-dashed border-secondary border-opacity-25 my-10" />
               <Footer />
          </>
     )
}

const Factures = ({ isEditionModeEnabled, onCRUDFacturesPrestationCallback }: { isEditionModeEnabled: boolean; onCRUDFacturesPrestationCallback?: () => void }) => {
     const { data } = useDeclarationUpdate()
     const queryClient = useQueryClient()
     const fileInputRef = useRef<HTMLInputElement>(null)

     const [isDragging, setIsDragging] = useState(false)
     const [isSending, setIsSending] = useState(false)
     const [isExtracting, setIsExtracting] = useState(false)
     const [processingId, setProcessingId] = useState<number | string | null>(null)
     const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])

     const getFacturesQuery = useQuery<IConsultantDeclarationMensuelleFactureModel[], AxiosError>(["GET_FACTURES_QUERY", data.declaration?.id], () =>
          declarationGetFacturesRequest(data.declaration?.id!).then(r => r.data)
     )

     const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files.length > 0) {
               addFiles(Array.from(e.target.files))
          }
          if (fileInputRef.current) fileInputRef.current.value = ""
     }

     const addFiles = async (files: File[]) => {
          if (!files || files.length === 0) return

          const formData = new FormData()
          files.forEach((file, index) => formData.append(`files[${index}]`, file))

          try {
               setIsExtracting(true)

               const response = await axios.post<IExtractionResponse>(API_EXTRACT_HT_TVA_FROM_INVOICE_URL, formData)
               toast.dismiss()

               if (response.data && response.data.invoices) {
                    const { invoices } = response.data

                    const extractedEntries = files.map((file, index) => {
                         const invoiceData = invoices[index]
                         const htValue = parseFloat(invoiceData?.totalAmountHt?.toString() || "0")
                         const vatValue = parseFloat(invoiceData?.totalVat?.toString() || "0")

                         return {
                              file,
                              ht: isNaN(htValue) ? 0 : htValue,
                              tva: isNaN(vatValue) ? 0 : Number(vatValue.toFixed(2)),
                         }
                    })

                    setPendingFiles(prev => [...prev, ...extractedEntries])
                    toast.success("Extraction terminée. Merci de vérifier les montants.")
               }
          } catch (err) {
               toast.dismiss()
               toast.error("Échec de l'extraction. Saisie manuelle requise.")
               const manualEntries = files.map(f => ({ file: f, ht: "" as const, tva: "" as const }))
               setPendingFiles(prev => [...prev, ...manualEntries])
          } finally {
               setIsExtracting(false)
          }
     }

     const handleAddFactures = () => {
          if (pendingFiles.length === 0) return

          const isInvalid = pendingFiles.some(item => {
               const ht = Number(item.ht)
               return isNaN(ht) || ht <= 0 || item.ht === "" || item.tva === ""
          })

          if (isInvalid) {
               toast.error("Veuillez saisir des montants HT et TVA valides.")
               return
          }

          setIsSending(true)
          const formData = new FormData()
          pendingFiles.forEach((item, index) => {
               formData.append(`factures[${index}]`, item.file)
               formData.append(`amountsHt[${index}]`, item.ht.toString())
               formData.append(`vats[${index}]`, item.tva.toString())
          })

          declarationAddFactureRequest(data.declaration?.id as number, formData)
               .then(() => {
                    toast.success("Factures enregistrées.")
                    setPendingFiles([])
                    getFacturesQuery.refetch().then(() => onCRUDFacturesPrestationCallback?.())
               })
               .finally(() => setIsSending(false))
     }

     const handleDeleteFacture = (factureId: number) => {
          swal.fire({
               icon: "warning",
               title: "Supprimer cette facture ?",
               showCancelButton: true,
               confirmButtonText: "Oui, supprimer",
               confirmButtonColor: "#f1416c",
               cancelButtonText: "Anuler",
          }).then(result => {
               if (result.isConfirmed) {
                    setProcessingId(factureId)
                    declarationDeleteFactureRequest(data.declaration?.id!, factureId)
                         .then(() => {
                              queryClient.setQueryData<IConsultantDeclarationMensuelleFactureModel[]>(["GET_FACTURES_QUERY", data.declaration?.id], old =>
                                   old ? old.filter(f => f.id !== factureId) : []
                              )
                              toast.success("Document supprimé.")
                              getFacturesQuery.refetch().then(() => onCRUDFacturesPrestationCallback?.())
                         })
                         .finally(() => setProcessingId(null))
               }
          })
     }

     const handleDownload = (googleDriveFileId: string, mime: string, docId: number) => {
          setProcessingId(`dl-${docId}`)
          axios.post(`${API_URL_ESPACE_AVENTURIER}/download-document`, { reference: googleDriveFileId }, { responseType: "blob" })
               .then(r => {
                    const url = window.URL.createObjectURL(new Blob([r.data]))
                    const link = document.createElement("a")
                    link.href = url
                    link.setAttribute("download", `facture.${getExtensionAccordingToMimetype(mime)}`)
                    document.body.appendChild(link)
                    link.click()
               })
               .finally(() => setProcessingId(null))
     }

     return (
          <>
               {isEditionModeEnabled && (
                    <>
                         {isExtracting ? (
                              <div className="rounded border border-primary border-dashed bg-light-primary p-12 mb-10 d-flex flex-column align-items-center justify-content-center shadow-sm">
                                   <div className="mb-5 position-relative">
                                        <div className="spinner-border text-primary" style={{ width: "3.5rem", height: "3.5rem", borderWidth: "3px" }} role="status"></div>
                                        <i className="fas fa-magic fs-4 text-primary position-absolute top-50 start-50 translate-middle"></i>
                                   </div>
                                   <h3 className="text-primary fw-bolder fs-3 mb-2">Analyse en cours ...</h3>
                                   <p className="text-gray-600 fw-semibold fs-6 text-center max-w-400px mb-0">Identification automatique des montants HT et TVA.</p>
                              </div>
                         ) : (
                              <Dropzone
                                   isDragging={isDragging}
                                   fileInputRef={fileInputRef}
                                   onDragOver={e => {
                                        e.preventDefault()
                                        setIsDragging(true)
                                   }}
                                   onDragLeave={() => setIsDragging(false)}
                                   onDrop={e => {
                                        e.preventDefault()
                                        setIsDragging(false)
                                        if (e.dataTransfer.files?.length > 0) addFiles(Array.from(e.dataTransfer.files))
                                   }}
                                   onFileSelect={handleFileSelect}
                              />
                         )}

                         {pendingFiles.length > 0 && (
                              <PendingQueue
                                   files={pendingFiles}
                                   isSending={isSending}
                                   isExtracting={isExtracting}
                                   onUpdateData={(idx, field, val) => {
                                        const updated = [...pendingFiles]
                                        updated[idx][field] = val === "" ? "" : parseFloat(val)
                                        setPendingFiles(updated)
                                   }}
                                   onRemove={idx => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                                   onSubmit={handleAddFactures}
                              />
                         )}
                    </>
               )}

               {getFacturesQuery.isFetching && !getFacturesQuery.data ? (
                    <div className="text-center py-10">
                         <MySimpleSpinner size="xl" />
                    </div>
               ) : (
                    <FacturesHistoryList
                         data={getFacturesQuery.data || []}
                         processingId={processingId}
                         isEditionModeEnabled={isEditionModeEnabled}
                         onDownload={handleDownload}
                         onDelete={handleDeleteFacture}
                    />
               )}
          </>
     )
}

export { DeclarationUpdateFactures, Factures }
